import * as React from 'react';
import { SHW_ENV } from '@showell-labs/env';
import {
  POWERPOINT_ADD_IN_URL_LOCAL,
  SHOWELL_APP_URL_LOCAL,
} from '@showell-labs/showell-commons/lib/commons/config';
import { useIntl } from 'react-intl';
import { useAuth } from './AuthContext';
import { loginMessages } from '../intl/messages';
import type { WorkspaceInfo } from './types';

const LOGOUT_APP_SESSION_KEY = 'powerpoint-add-in-logout-app-session';

/**
 * Message sent from the Showell App to the add-in after authentication.
 * The PowerPoint service in the Showell App sends this message.
 */
type AuthenticatedMessage = {
  type: 'powerpoint-authenticated';
  accessToken: string;
  accountName?: string;
  userName?: string;
  applications: WorkspaceInfo[];
};

type AuthenticateSSOMessage = {
  type: 'authenticate-sso';
  route: string;
};

type DismissAuthMessage = {
  type: 'dismiss-auth';
};

type IncomingMessage =
  | AuthenticatedMessage
  | AuthenticateSSOMessage
  | DismissAuthMessage;

function getAppUrl(): string {
  // The add-in loads its embedded Showell App at the dedicated
  // `SHW_POWERPOINT_APP_URL` host so that the in-iframe session stays on a
  // single, static origin — `services/login/commons.ts:isStaticHost` matches
  // this hostname and `LoginScreen` skips the custom-domain cross-host
  // redirect that would otherwise break the postMessage bridge back to the
  // add-in.
  return process.env.NODE_ENV === 'development'
    ? SHOWELL_APP_URL_LOCAL
    : SHW_ENV.SHW_POWERPOINT_APP_URL;
}

function getAddInOrigin(): string {
  return process.env.NODE_ENV === 'development'
    ? POWERPOINT_ADD_IN_URL_LOCAL
    : SHW_ENV.SHW_POWERPOINT_ADD_IN_URL;
}

export function LoginView() {
  const { login } = useAuth();
  const intl = useIntl();
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const dialogRef = React.useRef<Office.Dialog | null>(null);

  const appUrl = getAppUrl();
  const addInOrigin = getAddInOrigin();
  // Pre-compute the expected origin once (appUrl is a constant derived from env).
  const expectedOrigin = new URL(appUrl).origin;

  // Read and clear the logout flag exactly once using a ref initializer.
  // useMemo must not be used here because sessionStorage.removeItem is a side
  // effect and React may call useMemo callbacks more than once (strict mode).
  const shouldLogoutRef = React.useRef<boolean | null>(null);
  if (shouldLogoutRef.current === null) {
    try {
      const val = window.sessionStorage.getItem(LOGOUT_APP_SESSION_KEY);
      if (val === '1') {
        window.sessionStorage.removeItem(LOGOUT_APP_SESSION_KEY);
        shouldLogoutRef.current = true;
      } else {
        shouldLogoutRef.current = false;
      }
    } catch {
      // ignore session storage failures
      shouldLogoutRef.current = false;
    }
  }
  const shouldLogoutAppSession = shouldLogoutRef.current;

  const launchUrl =
    `${appUrl}/launch?` +
    new URLSearchParams({
      context: 'powerpoint',
      referrer: addInOrigin,
      mode: 'embedded',
      route: shouldLogoutAppSession ? '/logout' : '/login',
    }).toString();

  React.useEffect(() => {
    function onMessage(event: MessageEvent<IncomingMessage>) {
      // Validate the message comes from the Showell App (strict origin check).
      if (event.origin !== expectedOrigin) {
        return;
      }

      if (
        event.data?.type === 'powerpoint-authenticated' &&
        event.data.accessToken
      ) {
        login(
          event.data.accessToken,
          event.data.accountName,
          event.data.applications,
          event.data.userName
        );
        return;
      }

      if (event.data?.type === 'dismiss-auth') {
        dialogRef.current?.close();
        dialogRef.current = null;
        return;
      }

      if (event.data?.type === 'authenticate-sso') {
        dialogRef.current = null;
        const contentWindow = iframeRef.current?.contentWindow;

        const processDialogEvent = (
          arg: { error: number } | { message: string; origin?: string }
        ) => {
          const CLOSED_BY_USER = 12002;
          const CLOSED_BY_INVALID_URL = 12003;
          const CLOSED_BY_ADD_IN = 12006;
          if (!('error' in arg)) {
            return;
          }

          if (
            [CLOSED_BY_USER, CLOSED_BY_INVALID_URL, CLOSED_BY_ADD_IN].includes(
              arg.error
            )
          ) {
            contentWindow?.postMessage({ type: 'auth-dismissed' }, appUrl);
          }
        };

        Office.context.ui.displayDialogAsync(
          appUrl + event.data.route,
          { height: 80, width: 80 },
          asyncResult => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
              contentWindow?.postMessage(
                { type: 'auth-failed', message: asyncResult.error.message },
                appUrl
              );
            } else {
              dialogRef.current = asyncResult.value;
              dialogRef.current.addEventHandler(
                Office.EventType.DialogEventReceived,
                processDialogEvent
              );
            }
          }
        );
      }
    }

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      // Close any open SSO dialog to prevent orphaned event handlers.
      if (dialogRef.current) {
        dialogRef.current.close();
        dialogRef.current = null;
      }
    };
  }, [login, appUrl, expectedOrigin]);

  return (
    <iframe
      ref={iframeRef}
      src={launchUrl}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        margin: 0,
        padding: 0,
        display: 'block',
      }}
      title={intl.formatMessage(loginMessages.title)}
    />
  );
}
